/* ez-pdp-snap-tiers.js — the SHADE-ROLL snap-density mini-configurator.
   Source of truth: demo/app/products/shade-roll/[ft]/RollPDP.jsx §"2 · Snaps".

   Registers itself as an extension MODULE on the enclosing .ez-pdp buy-hero root (via
   root.ezRegisterModule). On every tier/colour/qty change it:
     • computes the chosen tier's SNA pack combo with the shared shade engine
       (window.EZSnap.createShadeEngine → rollLine(ft, tier)) — least-cost cover, NEVER typed;
     • writes each tier card's "+$price" (bound, in the current mesh colour);
     • contributes a "Snaps · <Tier> · <count>" summary row (price = the pack cost × qty);
     • contributes the SNA pack variant lines (in the mesh colour) to the multi-line ATC.

   The colour couples to the buy card's colour-card selection (ctx.color = 'Black'/'White'/…): the
   engine catalog is rebuilt per colour so the added SNA variants match the mesh. INSTANCE-SAFE:
   every query is scoped to this tier root + its parent .ez-pdp. */
(function () {
  'use strict';

  // colour NAME (the colour-card value: 'Black'/'White'/…) → SKU colour code (BK/WH/BG/GY).
  function colorCode(name) {
    var n = String(name || '').trim().toLowerCase();
    if (n === 'black' || n === 'bk') return 'BK';
    if (n === 'white' || n === 'wh') return 'WH';
    if (n === 'beige' || n === 'bg') return 'BG';
    if (n === 'grey' || n === 'gray' || n === 'gy') return 'GY';
    return 'BK';
  }

  function money(n) {
    n = Number(n) || 0;
    // non-store currency (Canada market = CAD): the ONE theme formatter, window.EZ (layout/theme.liquid)
    var E = window.EZ;
    if (E && E.isBase === false && typeof E.money === 'function') return E.money(n);
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  }

  function initTier(el) {
    if (!el || el.dataset.ezSnapInit === '1') return;
    var root = el.closest('.ez-pdp');
    if (!root) return;
    // need the engine + the buy-hero module API; retry until both are present.
    if (!(window.EZSnap && window.EZSnap.createShadeEngine) || typeof root.ezRegisterModule !== 'function') {
      return setTimeout(function () { initTier(el); }, 60);
    }
    el.dataset.ezSnapInit = '1';

    var cfgEl = el.querySelector('[data-ez-snap-catalog]');
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var ft = cfg.ft;

    // build one shade engine per colour on demand (catalog SNA/roll rows filtered to that colour).
    var engineCache = {};
    function engineFor(code) {
      if (engineCache[code]) return engineCache[code];
      var rolls = (cfg.rolls || []).filter(function (r) { return r.color === code; });
      var snaPacks = (cfg.snaPacks || []).filter(function (p) { return p.color === code; });
      // the roll's snap tiers live on cfg.tiers, not the catalog roll row — inject them.
      rolls = rolls.map(function (r) { return Object.assign({}, r, { snaps: cfg.tiers }); });
      var eng = window.EZSnap.createShadeEngine({ rolls: rolls, snaPacks: snaPacks });
      engineCache[code] = eng;
      return eng;
    }

    var tierInputs = Array.prototype.slice.call(el.querySelectorAll('[data-ez-snap-input]'));
    function currentTier() {
      var picked = el.querySelector('[data-ez-snap-input]:checked');
      return picked ? picked.value : (cfg.recommend || 'medium');
    }

    // the roll purchase line for a tier + colour (packs + price), via the shared engine.
    function rollLineFor(tier, colorName) {
      if (tier === 'none') return null;
      var code = colorCode(colorName);
      return engineFor(code).rollLine(ft, tier);
    }

    // paint every tier card's "+$price" for the current colour (bound).
    function paintPrices(colorName) {
      el.querySelectorAll('[data-ez-snap-tier]').forEach(function (card) {
        var tier = card.getAttribute('data-ez-snap-tier');
        var L = rollLineFor(tier, colorName);
        var priceEl = card.querySelector('[data-ez-snap-price]');
        if (priceEl) {
          var snapCost = L ? (L.price - (L.rollPrice || 0)) : 0;
          priceEl.textContent = '+' + money(snapCost);
        }
        // Show what actually SHIPS (the least-cost pack coverage), not the raw density
        // recommendation, so per-snap reads honestly (e.g. 75 snaps for $114.98, not 60).
        var countEl = card.querySelector('.ez-pdp__snaptier-count');
        if (countEl && L && L.delivered) {
          countEl.textContent = L.delivered + ' snaps';
        }
      });
    }

    // selected-state highlight on the tier cards + the "roll only" pill.
    function paintSelected() {
      var val = currentTier();
      el.querySelectorAll('[data-ez-snap-tier]').forEach(function (card) {
        card.classList.toggle('is-selected', card.getAttribute('data-ez-snap-tier') === val);
      });
      var none = el.querySelector('.ez-pdp__snaptier-none');
      if (none) none.classList.toggle('is-selected', val === 'none');
    }

    var TIER_LABEL = { light: 'Light', medium: 'Medium', heavy: 'Heavy' };

    // the snap cost (dollars) for the current tier + colour, per unit (before qty).
    function snapCostPerUnit(colorName) {
      var tier = currentTier();
      if (tier === 'none') return 0;
      var L = rollLineFor(tier, colorName);
      return L ? (L.price - (L.rollPrice || 0)) : 0;
    }

    // register with the buy-hero: contribute the snap summary row + the SNA pack ATC lines, and
    // drive the HEADLINE price so the roll's big number reads as the composed kit price (demo parity).
    root.ezRegisterModule({
      headlineAdd: function (ctx) {
        // headline shows the per-UNIT composed kit price (roll + snaps), like the demo `kitPrice`.
        return Math.round(snapCostPerUnit(ctx.color) * 100);
      },
      summary: function (ctx) {
        var tier = currentTier();
        if (tier === 'none') return [];
        var L = rollLineFor(tier, ctx.color);
        if (!L) return [];
        var snapCost = L.price - (L.rollPrice || 0);
        // the buy-hero summary/total pipeline works in CENTS (kit.price is data-price-cents) and
        // renders via money(cents) — so return the snap cost in CENTS, not dollars.
        return [{
          label: 'Snaps',
          meta: (TIER_LABEL[tier] || tier) + ' · ' + (L.delivered || L.snaps),
          price: Math.round(snapCost * 100) * (ctx.qty || 1)
        }];
      },
      lines: function (ctx) {
        var tier = currentTier();
        if (tier === 'none') return [];
        var L = rollLineFor(tier, ctx.color);
        if (!L) return [];
        var q = ctx.qty || 1;
        return (L.packs || []).filter(function (p) { return p.variantId; }).map(function (p) {
          return { id: p.variantId, quantity: p.count * q };
        });
      }
    });

    // tier change → repaint selection + ask the buy-hero to recompute the total/summary.
    tierInputs.forEach(function (inp) {
      inp.addEventListener('change', function () {
        paintSelected();
        if (typeof root.ezRecompute === 'function') root.ezRecompute();
      });
    });
    // colour change lives on the buy card's colour cards; recompute already fires there, but the
    // per-tier "+$price" labels must repaint to the new colour too — watch the colour inputs.
    root.querySelectorAll('.ez-pdp__colour-card input, .ez-pdp__swatch input').forEach(function (inp) {
      inp.addEventListener('change', function () { paintPrices(inp.value); });
    });

    // initial paint (colour = current selection) then hand a recompute to the buy-hero.
    function currentColorName() {
      var picked = root.querySelector('.ez-pdp__colour-card input:checked, .ez-pdp__swatch input:checked');
      return picked ? picked.value : 'Black';
    }
    paintPrices(currentColorName());
    paintSelected();
    if (typeof root.ezRecompute === 'function') root.ezRecompute();
  }

  function initAll() {
    document.querySelectorAll('[data-ez-snap-tiers]').forEach(initTier);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target || document;
    (t.querySelectorAll ? t.querySelectorAll('[data-ez-snap-tiers]') : []).forEach(initTier);
  });
})();
